import type { WebSocketMessage } from "@game/protocol";

let canvas: HTMLCanvasElement;
let state: any;
let worldToScreen: (x: number, y: number) => { x: number; y: number };

export function setCoordinateValidatorDependencies(
  canvasEl: HTMLCanvasElement,
  gameState: any,
  worldToScreenFn: (x: number, y: number) => { x: number; y: number },
) {
  canvas = canvasEl;
  state = gameState;
  worldToScreen = worldToScreenFn;
}

export class CoordinateValidator {
  static validateMessage(msg: WebSocketMessage) {
    try {
      if (!msg || typeof msg !== "object") return false;
      if (msg.t !== "damage_dealt") return false;
      if (typeof msg.x !== "number" || typeof msg.y !== "number") return false;
      if (typeof msg.damage !== "number" || typeof msg.source !== "string") {
        return false;
      }

      if (msg.damage < 0 || msg.damage > 999) return false;

      if (
        msg.source !== "bullet" &&
        msg.source !== "collision" &&
        msg.source !== "asteroid"
      ) {
        return false;
      }

      return true;
    } catch (error) {
      console.warn("Damage message validation error:", error);
      return false;
    }
  }

  static worldToScreen(worldX: number, worldY: number) {
    try {
      if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) {
        console.warn(
          `Invalid damage coordinates: worldX=${worldX}, worldY=${worldY}`,
        );
        return { x: canvas.width / 2, y: canvas.height / 2 };
      }

      if (!state.camera || !state.world) {
        console.warn("Camera or world state not initialized");
        return { x: canvas.width / 2, y: canvas.height / 2 };
      }

      const result = worldToScreen(worldX, worldY);
      return result;
    } catch (error) {
      console.warn("Coordinate transformation error:", error);
      return {
        x: canvas.width / 2,
        y: canvas.height / 2,
      };
    }
  }

  static preventOverlap(
    position: { x: number; y: number },
    activeDamageNumbers: any[],
  ) {
    const minDistance = 20;
    const maxAttempts = 8;
    let attempts = 0;
    let newX = position.x;
    let newY = position.y;

    while (attempts < maxAttempts) {
      let hasOverlap = false;

      for (const existingNumber of activeDamageNumbers) {
        if (!existingNumber.active) continue;

        const dx = newX - existingNumber.x;
        const dy = newY - existingNumber.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance) {
          hasOverlap = true;
          break;
        }
      }

      if (!hasOverlap) {
        break;
      }

      const angle = (attempts / maxAttempts) * Math.PI * 2;
      const radius = minDistance * (1 + attempts * 0.5);
      newX = position.x + Math.cos(angle) * radius;
      newY = position.y + Math.sin(angle) * radius;

      newX = Math.max(50, Math.min(canvas.width - 50, newX));
      newY = Math.max(50, Math.min(canvas.height - 50, newY));

      attempts++;
    }

    return { x: newX, y: newY };
  }
}
